import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Ban, FlaskConical, Loader2, Pause, Pencil, Play, Plus, Rocket, Trash2, X } from "lucide-react";
import {
  CAMPAIGN_STATUS_META,
  CATEGORIES,
  CHANNELS,
  HOLDOUT_HELP,
  SEND_TIME_HELP,
  channelLabel,
  errorMessage,
  fetchConversionGoals,
  fetchJson,
  formatDateTime,
  formatNumber,
  isoToLocalInput,
  localInputToIso,
  type Campaign,
  type CampaignDetail,
  type CampaignStatus,
  type Category,
  type Channel,
  type ConversionGoal,
  type SegmentsResponse,
  type TemplatesResponse,
} from "@/lib/comms";
import { EmptyState, ErrorState, StatusBadge, TableSkeleton } from "./shared";
import CampaignReportPanel from "./CampaignReportPanel";
import ConversionPanel from "./ConversionPanel";

const MAX_AB_SUBJECTS = 4;
const GOALS_KEY = ["/api/admin/comms/conversions/goals"];

/** Shared by the create dialog and the detail sheet, so a goal is never shown as a raw id. */
function useConversionGoals() {
  return useQuery<{ goals: ConversionGoal[] }>({ queryKey: GOALS_KEY, queryFn: fetchConversionGoals });
}

const CONFIRM_WORD = "SEND";
const CONFIRM_THRESHOLD = 500;

interface Draft {
  id: string | null;
  name: string;
  channel: Channel;
  category: Category;
  segmentId: string;
  templateId: string;
  holdoutPercent: string;
  abSubjects: string[];
  sendTimeOptimized: boolean;
  conversionGoal: string;
  conversionWindowDays: string;
}

const EMPTY_DRAFT: Draft = {
  id: null,
  name: "",
  channel: "email",
  category: "learning",
  segmentId: "",
  templateId: "",
  holdoutPercent: "0",
  abSubjects: ["", ""],
  sendTimeOptimized: false,
  conversionGoal: "none",
  conversionWindowDays: "7",
};

function clampNumber(raw: string, min: number, max: number, fallback: number): number {
  const n = Number(raw);
  if (raw.trim() === "" || Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function StatusPill({ status }: { status: CampaignStatus }) {
  const meta = CAMPAIGN_STATUS_META[status] || { label: status, className: "bg-muted text-muted-foreground" };
  return <Badge className={`border-0 ${meta.className}`}>{meta.label}</Badge>;
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-xl font-bold">{formatNumber(value)}</p>
    </div>
  );
}

function LaunchDialog({
  campaign,
  segmentCount,
  open,
  onOpenChange,
}: {
  campaign: Campaign;
  segmentCount: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [mode, setMode] = useState<"now" | "schedule">("now");
  const [when, setWhen] = useState("");
  const [respectCap, setRespectCap] = useState(true);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (open) {
      setMode("now");
      setWhen(isoToLocalInput(campaign.scheduledAt) || "");
      setRespectCap(true);
      setConfirmText("");
    }
  }, [open, campaign.scheduledAt]);

  const needsConfirm = (segmentCount ?? 0) > CONFIRM_THRESHOLD;
  const confirmed = !needsConfirm || confirmText.trim() === CONFIRM_WORD;

  const launchMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { respectCap };
      if (mode === "schedule") {
        const iso = localInputToIso(when);
        if (!iso) throw new Error("Pick a valid date and time to schedule this campaign.");
        body.scheduledAt = iso;
      }
      return (await apiRequest("POST", `/api/admin/comms/campaigns/${campaign.id}/launch`, body)).json();
    },
    onSuccess: (res: { queued?: number; skippedCapped?: number; skippedNoAudience?: number; holdout?: number; skippedConsent?: number }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/campaigns", campaign.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/overview"] });
      onOpenChange(false);
      const parts = [
        `${formatNumber(res.queued)} queued`,
        `${formatNumber(res.skippedCapped)} skipped by the frequency cap`,
      ];
      if (res.skippedNoAudience) parts.push(`${formatNumber(res.skippedNoAudience)} with no reachable address`);
      if (res.holdout) parts.push(`${formatNumber(res.holdout)} held back as the holdout`);
      if (res.skippedConsent) parts.push(`${formatNumber(res.skippedConsent)} skipped for consent`);
      toast({
        title: mode === "schedule" ? "Campaign scheduled" : "Campaign launched",
        description: `${parts.join(", ")}.`,
      });
    },
    onError: (e: unknown) => toast({ title: "Launch failed", description: errorMessage(e), variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Launch {campaign.name}</DialogTitle>
          <DialogDescription>
            The audience is resolved now, not when the campaign was created. Suppressed, unsubscribed and unreachable contacts are dropped
            automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border p-3 text-sm space-y-1">
            <p>
              Segment <span className="font-medium">{campaign.segmentName || "not set"}</span> last counted{" "}
              <span className="font-semibold">{segmentCount === null || segmentCount === undefined ? "never" : formatNumber(segmentCount)}</span>
              {segmentCount !== null && segmentCount !== undefined ? " contacts" : ""}.
            </p>
            <p className="text-muted-foreground text-xs">
              Recount the segment on the Segments tab if that number looks stale. The real audience is measured again at launch.
            </p>
          </div>

          <div className="space-y-2">
            <Label>When</Label>
            <Select value={mode} onValueChange={v => setMode(v as "now" | "schedule")}>
              <SelectTrigger aria-label="Launch timing"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="now">Send now</SelectItem>
                <SelectItem value="schedule">Schedule for later</SelectItem>
              </SelectContent>
            </Select>
            {mode === "schedule" && (
              <Input
                type="datetime-local"
                value={when}
                onChange={e => setWhen(e.target.value)}
                aria-label="Scheduled date and time"
                data-testid="input-schedule-at"
              />
            )}
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="respect-cap"
              checked={respectCap}
              onCheckedChange={v => setRespectCap(v === true)}
              data-testid="checkbox-respect-cap"
            />
            <div className="space-y-0.5">
              <Label htmlFor="respect-cap" className="cursor-pointer">Respect the 3-day frequency cap</Label>
              <p className="text-xs text-muted-foreground">
                Skips anyone who already got something from us in the last few days. Turn it off only for a genuinely urgent notice.
              </p>
            </div>
          </div>

          {needsConfirm && (
            <div className="space-y-1.5">
              <Label htmlFor="confirm-send">
                This reaches more than {formatNumber(CONFIRM_THRESHOLD)} people. Type {CONFIRM_WORD} to confirm.
              </Label>
              <Input
                id="confirm-send"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                autoComplete="off"
                data-testid="input-confirm-send"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => launchMutation.mutate()}
            disabled={!confirmed || launchMutation.isPending}
            data-testid="button-confirm-launch"
          >
            {launchMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Launching…</> : mode === "schedule" ? "Schedule" : "Send now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignSheet({
  campaignId,
  segmentCount,
  onEdit,
}: {
  campaignId: string;
  segmentCount: number | null;
  onEdit: (campaign: Campaign) => void;
}) {
  const { toast } = useToast();
  const [launchOpen, setLaunchOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const { data, isLoading, error } = useQuery<CampaignDetail>({
    queryKey: ["/api/admin/comms/campaigns", campaignId],
    queryFn: () => fetchJson<CampaignDetail>(`/api/admin/comms/campaigns/${campaignId}`),
  });

  const actionMutation = useMutation({
    mutationFn: async (action: "pause" | "resume" | "cancel") =>
      (await apiRequest("POST", `/api/admin/comms/campaigns/${campaignId}/${action}`)).json(),
    onSuccess: (_res, action) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/campaigns", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/overview"] });
      setCancelOpen(false);
      toast({ title: action === "pause" ? "Campaign paused" : action === "resume" ? "Campaign resumed" : "Campaign cancelled" });
    },
    onError: (e: unknown) => toast({ title: "That did not work", description: errorMessage(e), variant: "destructive" }),
  });

  const { data: goalsData } = useConversionGoals();
  const campaign = data?.campaign;
  const stats = data?.stats || campaign?.stats;
  const sends = data?.sends || [];
  const conversions = data?.conversions || null;
  const total = Math.max(0, Number(stats?.total || 0));
  const sent = Math.max(0, Number(stats?.sent || 0));
  const pct = total > 0 ? Math.min(100, Math.round((sent / total) * 100)) : 0;

  return (
    <>
      <SheetHeader className="mb-4">
        <SheetTitle>{campaign?.name || "Campaign"}</SheetTitle>
        <SheetDescription>
          {campaign
            ? `${channelLabel(campaign.channel)} · ${campaign.category} · segment ${campaign.segmentName || "not set"} · template ${campaign.templateName || "not set"}`
            : "Loading the campaign."}
        </SheetDescription>
      </SheetHeader>

      {error ? (
        <ErrorState error={error} />
      ) : isLoading || !campaign || !stats ? (
        <div className="space-y-4">
          <Skeleton className="h-10" />
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
          </div>
          <TableSkeleton rows={6} cols={3} />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={campaign.status} />
            {campaign.scheduledAt ? <span className="text-xs text-muted-foreground">Scheduled for {formatDateTime(campaign.scheduledAt)}</span> : null}
            {campaign.startedAt ? <span className="text-xs text-muted-foreground">Started {formatDateTime(campaign.startedAt)}</span> : null}
            {campaign.completedAt ? <span className="text-xs text-muted-foreground">Finished {formatDateTime(campaign.completedAt)}</span> : null}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={!["draft", "scheduled", "paused"].includes(campaign.status)}
              onClick={() => setLaunchOpen(true)}
              data-testid="button-launch-campaign"
            >
              <Rocket className="w-4 h-4 mr-2" /> Launch
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={campaign.status !== "sending" || actionMutation.isPending}
              onClick={() => actionMutation.mutate("pause")}
              data-testid="button-pause-campaign"
            >
              <Pause className="w-4 h-4 mr-2" /> Pause
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={campaign.status !== "paused" || actionMutation.isPending}
              onClick={() => actionMutation.mutate("resume")}
              data-testid="button-resume-campaign"
            >
              <Play className="w-4 h-4 mr-2" /> Resume
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={["sent", "cancelled"].includes(campaign.status) || actionMutation.isPending}
              onClick={() => setCancelOpen(true)}
              data-testid="button-cancel-campaign"
            >
              <Ban className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!["draft", "scheduled", "paused"].includes(campaign.status)}
              onClick={() => onEdit(campaign)}
              data-testid="button-edit-from-sheet"
            >
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </Button>
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <p className="text-sm font-medium">Progress</p>
              <span className="text-sm text-muted-foreground">{formatNumber(sent)} of {formatNumber(total)} sent</span>
            </div>
            <Progress value={pct} className="h-2" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatTile label="Queued" value={stats.queued} />
            <StatTile label="Sent" value={stats.sent} />
            <StatTile label="Delivered" value={stats.delivered} />
            <StatTile label="Opened" value={stats.opened} />
            <StatTile label="Clicked" value={stats.clicked} />
            <StatTile label="Bounced" value={stats.bounced} />
            <StatTile label="Complained" value={stats.complained} />
            <StatTile label="Failed" value={stats.failed} />
            <StatTile label="Sandboxed" value={stats.sandboxed} />
            <StatTile label="Skipped by cap" value={stats.skippedCapped} />
            <StatTile label="Holdout" value={stats.holdout} />
            <StatTile label="Converted" value={stats.converted} />
          </div>

          {conversions && <ConversionPanel report={conversions} goals={goalsData?.goals} />}

          {["sending", "paused", "sent", "cancelled"].includes(campaign.status) && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Report</p>
              <CampaignReportPanel campaignId={campaignId} />
            </div>
          )}

          <div>
            <p className="text-sm font-medium mb-2">Last {Math.min(100, sends.length)} sends</p>
            {sends.length === 0 ? (
              <EmptyState title="Nothing sent yet" hint="Sends appear here once the campaign is launched and the worker picks the queue up." />
            ) : (
              <div className="max-h-[420px] overflow-y-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Address</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sends.map(s => (
                      <TableRow key={s.id}>
                        <TableCell className="text-xs break-all">
                          {s.address}
                          {s.error ? <p className="text-xs text-destructive">{s.error}</p> : null}
                        </TableCell>
                        <TableCell><StatusBadge status={s.status} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDateTime(s.sentAt || s.scheduledFor)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      )}

      {campaign && (
        <LaunchDialog campaign={campaign} segmentCount={segmentCount} open={launchOpen} onOpenChange={setLaunchOpen} />
      )}

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              Everything still queued is dropped and the campaign cannot be resumed. Messages already sent stay sent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => actionMutation.mutate("cancel")}
              data-testid="button-confirm-cancel-campaign"
            >
              {actionMutation.isPending ? "Cancelling…" : "Cancel campaign"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function CampaignsTab() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Campaign | null>(null);

  const { data, isLoading, error } = useQuery<{ campaigns: Campaign[] }>({ queryKey: ["/api/admin/comms/campaigns"] });
  const { data: segmentsData } = useQuery<SegmentsResponse>({ queryKey: ["/api/admin/comms/segments"] });
  const { data: templatesData } = useQuery<TemplatesResponse>({ queryKey: ["/api/admin/comms/templates"] });
  const { data: goalsData } = useConversionGoals();
  const goals = goalsData?.goals || [];

  const campaigns = data?.campaigns || [];
  const segments = segmentsData?.segments || [];
  const templates = templatesData?.templates || [];

  const templatesForChannel = useMemo(
    () => templates.filter(t => t.channel === draft.channel),
    [templates, draft.channel],
  );

  const openCampaign = campaigns.find(c => c.id === openId) || null;
  const openSegmentCount = useMemo(() => {
    if (!openCampaign?.segmentId) return null;
    const seg = segments.find(s => s.id === openCampaign.segmentId);
    return seg?.lastCount ?? null;
  }, [openCampaign, segments]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/campaigns"] });

  const saveMutation = useMutation({
    mutationFn: async (payload: { id: string | null; body: Record<string, unknown> }) => {
      const res = payload.id
        ? await apiRequest("PATCH", `/api/admin/comms/campaigns/${payload.id}`, payload.body)
        : await apiRequest("POST", "/api/admin/comms/campaigns", payload.body);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      if (draft.id) queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/campaigns", draft.id] });
      setDialogOpen(false);
      toast({ title: draft.id ? "Campaign updated" : "Campaign created" });
    },
    onError: (e: unknown) => {
      setFormError(errorMessage(e));
      toast({ title: "Could not save the campaign", description: errorMessage(e), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await apiRequest("DELETE", `/api/admin/comms/campaigns/${id}`)).json(),
    onSuccess: (_res, id) => {
      invalidate();
      setDeleteTarget(null);
      if (openId === id) setOpenId(null);
      toast({ title: "Campaign deleted" });
    },
    onError: (e: unknown) => toast({ title: "Could not delete the campaign", description: errorMessage(e), variant: "destructive" }),
  });

  const openCreate = () => {
    setDraft(EMPTY_DRAFT);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (c: Campaign) => {
    const subjects = (c.abSubjects || []).slice(0, MAX_AB_SUBJECTS);
    setDraft({
      id: c.id,
      name: c.name,
      channel: c.channel,
      category: c.category,
      segmentId: c.segmentId || "",
      templateId: c.templateId || "",
      holdoutPercent: String(c.holdoutPercent ?? 0),
      abSubjects: subjects.length >= 2 ? subjects : ["", ""],
      sendTimeOptimized: !!c.sendTimeOptimized,
      conversionGoal: c.conversionGoal || "none",
      conversionWindowDays: String(c.conversionWindowDays ?? 7),
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const setSubject = (index: number, value: string) =>
    setDraft(d => ({ ...d, abSubjects: d.abSubjects.map((s, i) => (i === index ? value : s)) }));

  const addSubject = () =>
    setDraft(d => (d.abSubjects.length >= MAX_AB_SUBJECTS ? d : { ...d, abSubjects: [...d.abSubjects, ""] }));

  const removeSubject = (index: number) =>
    setDraft(d => (d.abSubjects.length <= 2 ? d : { ...d, abSubjects: d.abSubjects.filter((_, i) => i !== index) }));

  const submit = () => {
    setFormError(null);
    if (!draft.name.trim()) {
      setFormError("Give the campaign a name.");
      return;
    }
    if (!draft.segmentId) {
      setFormError("Pick the segment this goes to.");
      return;
    }
    if (!draft.templateId) {
      setFormError("Pick the template to send.");
      return;
    }
    const filledSubjects = draft.abSubjects.map(s => s.trim()).filter(Boolean);
    saveMutation.mutate({
      id: draft.id,
      body: {
        name: draft.name.trim(),
        channel: draft.channel,
        category: draft.category,
        segmentId: draft.segmentId,
        templateId: draft.templateId,
        holdoutPercent: clampNumber(draft.holdoutPercent, 0, 50, 0),
        // Fewer than two filled lines is not a test, it is just a subject. Send nothing.
        abSubjects: filledSubjects.length >= 2 ? filledSubjects.slice(0, MAX_AB_SUBJECTS) : [],
        sendTimeOptimized: draft.sendTimeOptimized,
        conversionGoal: draft.conversionGoal,
        conversionWindowDays: clampNumber(draft.conversionWindowDays, 1, 30, 7),
      },
    });
  };

  const selectedGoal = goals.find(g => g.id === draft.conversionGoal) || null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Campaigns</CardTitle>
              <CardDescription>
                A campaign is one segment plus one template, sent once. Launching resolves the audience, queues a send per contact, then the worker
                drains the queue inside the daily cap and outside quiet hours.
              </CardDescription>
            </div>
            <Button onClick={openCreate} data-testid="button-new-campaign">
              <Plus className="w-4 h-4 mr-2" /> New campaign
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <ErrorState error={error} />
          ) : isLoading ? (
            <TableSkeleton rows={6} cols={7} />
          ) : campaigns.length === 0 ? (
            <EmptyState
              title="No campaigns yet"
              hint="Nothing has been created. Build a segment and a template first, then a campaign ties the two together."
              action={<Button size="sm" onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> New campaign</Button>}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Segment</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Results</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map(c => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer"
                      onClick={() => setOpenId(c.id)}
                      data-testid={`row-campaign-${c.id}`}
                    >
                      <TableCell>
                        <p className="font-medium">{c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.category} · updated {formatDateTime(c.updatedAt)}</p>
                      </TableCell>
                      <TableCell><Badge variant="outline">{channelLabel(c.channel)}</Badge></TableCell>
                      <TableCell className="text-sm">{c.segmentName || "Not set"}</TableCell>
                      <TableCell className="text-sm">{c.templateName || "Not set"}</TableCell>
                      <TableCell><StatusPill status={c.status} /></TableCell>
                      <TableCell className="text-sm">{c.audienceCount === null || c.audienceCount === undefined ? "n/a" : formatNumber(c.audienceCount)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatNumber(c.stats?.sent)} sent · {formatNumber(c.stats?.opened)} opened · {formatNumber(c.stats?.clicked)} clicked ·{" "}
                        {formatNumber(c.stats?.bounced)} bounced
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Delete campaign"
                          onClick={() => setDeleteTarget(c)}
                          data-testid={`button-delete-campaign-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft.id ? "Edit campaign" : "New campaign"}</DialogTitle>
            <DialogDescription>Only draft, scheduled and paused campaigns can be edited.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="campaign-name">Name</Label>
              <Input
                id="campaign-name"
                value={draft.name}
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                placeholder="e.g. September re-engagement, Grade 5 parents"
                data-testid="input-campaign-name"
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="campaign-channel">Channel</Label>
                <Select
                  value={draft.channel}
                  onValueChange={v => setDraft(d => ({ ...d, channel: v as Channel, templateId: "" }))}
                >
                  <SelectTrigger id="campaign-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CHANNELS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="campaign-category">Category</Label>
                <Select value={draft.category} onValueChange={v => setDraft(d => ({ ...d, category: v as Category }))}>
                  <SelectTrigger id="campaign-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Marketing never goes to student contacts. Only parents receive it, unless that is deliberately turned on in Settings.
            </p>

            <div className="space-y-1.5">
              <Label htmlFor="campaign-segment">Segment</Label>
              <Select value={draft.segmentId} onValueChange={v => setDraft(d => ({ ...d, segmentId: v }))}>
                <SelectTrigger id="campaign-segment">
                  <SelectValue placeholder={segments.length === 0 ? "No segments saved yet" : "Choose a segment"} />
                </SelectTrigger>
                <SelectContent>
                  {segments.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{s.lastCount === null || s.lastCount === undefined ? "" : ` (${formatNumber(s.lastCount)})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="campaign-template">Template</Label>
              <Select value={draft.templateId} onValueChange={v => setDraft(d => ({ ...d, templateId: v }))}>
                <SelectTrigger id="campaign-template">
                  <SelectValue placeholder={templatesForChannel.length === 0 ? `No ${channelLabel(draft.channel).toLowerCase()} template yet` : "Choose a template"} />
                </SelectTrigger>
                <SelectContent>
                  {templatesForChannel.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Only templates on the campaign's channel are listed.</p>
            </div>

            {/* Experiments and measurement */}
            <div className="rounded-md border p-4 space-y-4">
              <div>
                <p className="text-sm font-medium flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Experiments and measurement</p>
                <p className="text-xs text-muted-foreground">
                  Optional, and worth the trouble. Without a holdout you can see who opened the message but never whether the message caused
                  anything.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="campaign-holdout">Holdout, percent</Label>
                  <Input
                    id="campaign-holdout"
                    type="number"
                    min={0}
                    max={50}
                    value={draft.holdoutPercent}
                    onChange={e => setDraft(d => ({ ...d, holdoutPercent: e.target.value }))}
                    data-testid="input-campaign-holdout"
                  />
                  <p className="text-xs text-muted-foreground">{HOLDOUT_HELP}</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="campaign-window">Conversion window, days</Label>
                  <Input
                    id="campaign-window"
                    type="number"
                    min={1}
                    max={30}
                    value={draft.conversionWindowDays}
                    onChange={e => setDraft(d => ({ ...d, conversionWindowDays: e.target.value }))}
                    data-testid="input-campaign-window"
                  />
                  <p className="text-xs text-muted-foreground">How long after a send an action still counts as caused by it. 1 to 30.</p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="campaign-goal">Conversion goal</Label>
                <Select value={draft.conversionGoal} onValueChange={v => setDraft(d => ({ ...d, conversionGoal: v }))}>
                  <SelectTrigger id="campaign-goal" data-testid="select-campaign-goal">
                    <SelectValue placeholder={goals.length === 0 ? "Loading goals" : "Choose a goal"} />
                  </SelectTrigger>
                  <SelectContent>
                    {goals.map(g => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {selectedGoal?.description ? (
                  <p className="text-xs text-muted-foreground">{selectedGoal.description}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">What counts as this campaign having worked.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Subject lines to test</Label>
                {draft.abSubjects.map((subject, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <span className="text-xs font-mono text-muted-foreground w-4 shrink-0">{String.fromCharCode(65 + i)}</span>
                    <Input
                      value={subject}
                      onChange={e => setSubject(i, e.target.value)}
                      placeholder={i === 0 ? "e.g. {{childFirstName}} moved up this week" : "A different way of saying it"}
                      data-testid={`input-ab-subject-${i}`}
                    />
                    {draft.abSubjects.length > 2 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeSubject(i)} title="Remove this line">
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    Fill in at least two and each send gets one at random, recorded as variant A, B, C or D. Leave them blank and the template's own
                    subject is used.
                  </p>
                  {draft.abSubjects.length < MAX_AB_SUBJECTS && (
                    <Button type="button" size="sm" variant="outline" onClick={addSubject} data-testid="button-add-ab-subject">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="min-w-0">
                  <Label htmlFor="campaign-sto" className="cursor-pointer">Send at the best time</Label>
                  <p className="text-xs text-muted-foreground leading-snug">{SEND_TIME_HELP}</p>
                </div>
                <Switch
                  id="campaign-sto"
                  checked={draft.sendTimeOptimized}
                  onCheckedChange={v => setDraft(d => ({ ...d, sendTimeOptimized: v }))}
                  data-testid="switch-campaign-sto"
                />
              </div>
            </div>

            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saveMutation.isPending} data-testid="button-save-campaign">
              {saveMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : draft.id ? "Save changes" : "Create campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail sheet */}
      <Sheet open={!!openId} onOpenChange={o => { if (!o) setOpenId(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {openId && (
            <CampaignSheet
              campaignId={openId}
              segmentCount={openSegmentCount}
              onEdit={c => { setOpenId(null); openEdit(c); }}
            />
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={o => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name} and its send history come off the record. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              data-testid="button-confirm-delete-campaign"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
